export const DOCUMENT_HISTORY_CHECKPOINT_INTERVAL_MS = 5 * 60 * 1000;

type CheckpointSchedulerOptions = {
  save: () => Promise<void>;
  onSavingChange?: (isSaving: boolean) => void;
  onSaved?: (hasPendingChanges: boolean) => void;
  onError?: (error: unknown) => void;
  intervalMs?: number;
};

export const createDocumentHistoryCheckpointScheduler = ({
  save,
  onSavingChange,
  onSaved,
  onError,
  intervalMs = DOCUMENT_HISTORY_CHECKPOINT_INTERVAL_MS,
}: CheckpointSchedulerOptions) => {
  let changeVersion = 0;
  let checkpointedChangeVersion = 0;
  let isSaving = false;
  let isDisposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const hasChanges = () => changeVersion > checkpointedChangeVersion;

  const clearTimer = () => {
    if (timer == undefined) return;

    clearTimeout(timer);
    timer = undefined;
  };

  const schedule = () => {
    if (isDisposed || !hasChanges() || isSaving || timer != undefined) return;

    timer = setTimeout(() => {
      timer = undefined;
      void checkpoint();
    }, intervalMs);
  };

  async function checkpoint({ force = false }: { force?: boolean } = {}) {
    if (isDisposed || isSaving || (!force && !hasChanges())) return false;

    clearTimer();
    isSaving = true;
    onSavingChange?.(true);
    const changeVersionAtStart = changeVersion;

    try {
      await save();
      checkpointedChangeVersion = Math.max(
        checkpointedChangeVersion,
        changeVersionAtStart
      );
      onSaved?.(hasChanges());
      return true;
    } catch (error) {
      onError?.(error);
      return false;
    } finally {
      isSaving = false;
      onSavingChange?.(false);
      schedule();
    }
  }

  const markDirty = () => {
    if (isDisposed) return;

    changeVersion++;
    schedule();
  };

  const dispose = () => {
    isDisposed = true;
    clearTimer();
  };

  return {
    checkpoint,
    dispose,
    hasChanges,
    markDirty,
  };
};
